/**
 * Drop-in replacement for src/routes/Mobile.js.
 *
 * New AI landing pages (HomePage, AgentsPage, IndustriesPage, CompanyPage) are
 * wired up alongside the existing legacy routes so all old links continue working.
 * /company/about-us and /company/contact-us both point to the new CompanyPage,
 * which scrolls to #about / #contact automatically.
 */

import React, { useLayoutEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";

// New AI landing
import HomePage from "pages/AI/HomePage";
import AgentsPage from "pages/AI/AgentsPage";
import IndustriesPage from "pages/AI/IndustriesPage";
import CompanyPage from "pages/AI/CompanyPage";

// Existing legacy pages — kept so deep links and old SEO don't break
import LandingForFilm from "pages/Landing/Film";
import LandingForHealth from "pages/Landing/Health";
import LandingForElectronics from "pages/Landing/Electronics";
import LandingForFood from "pages/Landing/Food";
import BlogPostPage from "pages/Landing/Home/BlogPostPage";
import SingleBlogPage from "pages/Landing/Home/SingleBlogPage";
import ErrorPage from "pages/Landing/Error";
import RefundPolicy from "pages/Landing/Policies/RefundPolicy";
import PrivacyPolicy from "pages/Landing/Policies/PrivacyPolicy";
import TermsCondition from "pages/Landing/Policies/TermsCondition";
import LandingForHotel from "pages/Landing/Hotel";
import LandingForCustomerManagement from "pages/Landing/CustomerManagement";
import LandingForDeliveryManagement from "pages/Landing/DeliveryManagement";
import LandingForDistributorManagement from "pages/Landing/DistributorManagement";
import LandingForInventoryManagement from "pages/Landing/InventoryManagement";
import LandingForSaleForceManagement from "pages/Landing/SalesForceManagement";
import LandingForWareHouseManagement from "pages/Landing/WareHouseManagement";
import LandingForARAutomation from "pages/Landing/ARAutomation";
import LandingForB2BMarketplace from "pages/Landing/B2BMarketplace";
import LandingForEmbeddedFinancing from "pages/Landing/EmbeddedFinancing";
import LandingForSupplierManagement from "pages/Landing/SupplierManagement";
import UpdatePricingPage from "pages/UpdatedLanding/Pricing/UpdatePricingPage";

function Mobile() {
  const { pathname, hash } = useLocation();
  useLayoutEffect(() => {
    // Don't reset scroll when navigating to a #hash — the page handles that itself
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <Routes>
      {/* New AI landing */}
      <Route path="/" element={<HomePage />} />
      <Route path="/agents" element={<AgentsPage />} />
      <Route path="/industries" element={<IndustriesPage />} />
      <Route path="/company" element={<CompanyPage />} />
      <Route path="/company/about-us" element={<CompanyPage />} />
      <Route path="/company/contact-us" element={<CompanyPage />} />
      <Route path="/company/our-impact" element={<Navigate to="/company" replace />} />
      <Route path="/company/our-news" element={<Navigate to="/company" replace />} />

      {/* Blog */}
      <Route path="/post/en" element={<BlogPostPage />} />
      <Route path="/post/en/:id" element={<SingleBlogPage />} />

      {/* Pricing */}
      <Route path="/pricing" element={<UpdatePricingPage />} />

      {/* Legacy industry pages (kept for SEO + deep links) */}
      <Route path="/industry/film" element={<LandingForFilm />} />
      <Route path="/industry/healthcare" element={<LandingForHealth />} />
      <Route path="/industry/hotel" element={<LandingForHotel />} />
      <Route path="/industry/electronics" element={<LandingForElectronics />} />
      <Route path="/industry/c-goods" element={<LandingForFood />} />

      {/* Legacy product pages */}
      <Route path="/product/ar-automation" element={<LandingForARAutomation />} />
      <Route path="/product/b2b" element={<LandingForB2BMarketplace />} />
      <Route path="/product/customer" element={<LandingForCustomerManagement />} />
      <Route path="/product/delivery" element={<LandingForDeliveryManagement />} />
      <Route path="/product/distributor" element={<LandingForDistributorManagement />} />
      <Route path="/product/financing" element={<LandingForEmbeddedFinancing />} />
      <Route path="/product/inventory" element={<LandingForInventoryManagement />} />
      <Route path="/product/salesforce" element={<LandingForSaleForceManagement />} />
      <Route path="/product/supplier" element={<LandingForSupplierManagement />} />
      <Route path="/product/warehouse" element={<LandingForWareHouseManagement />} />

      {/* Policies */}
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-conditions" element={<TermsCondition />} />

      <Route path="*" element={<ErrorPage />} />
    </Routes>
  );
}

export default Mobile;
